import { requestJson } from '../lib/http';
import type { components } from '@/lib/api.generated';

/**
 * Installation-wide settings skills: assistant config + system health/version.
 *
 * Mirrors the envVars skill (requestJson + method). Types come from the generated
 * OpenAPI spec (`@/lib/api.generated`) — never `@/lib/api` — so the console stays
 * inside its isolation boundary. Two write scopes: the /api/config/* verbs
 * persist install-wide to ~/.archon/config.yaml (no repo overrides), and the
 * /api/auth/me/ai-prefs* verbs persist the caller's per-user prefs row.
 */

export type SafeConfig = components['schemas']['SafeConfig'];
export type ConfigResponse = components['schemas']['ConfigResponse'];
export type UpdateAssistantConfigBody = components['schemas']['UpdateAssistantConfigBody'];
export type HealthResponse = components['schemas']['HealthResponse'];
export type UpdateCheckResponse = components['schemas']['UpdateCheckResponse'];

export function getConfig(): Promise<ConfigResponse> {
  return requestJson<ConfigResponse>('/api/config');
}

export function updateAssistantConfig(body: UpdateAssistantConfigBody): Promise<ConfigResponse> {
  return requestJson<ConfigResponse>('/api/config/assistants', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function getHealth(): Promise<HealthResponse> {
  return requestJson<HealthResponse>('/api/health');
}

export function getUpdateCheck(): Promise<UpdateCheckResponse> {
  return requestJson<UpdateCheckResponse>('/api/update-check');
}

/**
 * Editable assistant form state. `models` is providerId → free-text model (model
 * strings are intentionally unvalidated — the SDK ships models faster than Archon
 * can enumerate them). The codex-only reasoning/web-search fields are flat here
 * and only attached to the codex entry by `buildAssistantUpdate`.
 */
export interface AssistantConfigForm {
  assistant: string;
  models: Record<string, string>;
  modelReasoningEffort: string;
  webSearchMode: string;
}

/**
 * Pure form → PATCH-body transform. Omits a provider's `model` when blank (so we
 * never overwrite a saved model with `''`) and drops a provider entirely when it
 * contributes no fields. Codex additionally carries `modelReasoningEffort` /
 * `webSearchMode` when set.
 *
 * Safety note: the PATCH route validates only provider *ids* and merges the body
 * into config.yaml UNFILTERED — per-field safe-filtering runs on the read path, not
 * the write path. So it matters that this function only ever attaches the codex-only
 * fields to the `codex` entry (it does); it must not leak them onto other providers.
 */
export function buildAssistantUpdate(form: AssistantConfigForm): UpdateAssistantConfigBody {
  const assistants: Record<string, Record<string, unknown>> = {};
  for (const [providerId, rawModel] of Object.entries(form.models)) {
    const entry: Record<string, unknown> = {};
    const model = rawModel.trim();
    if (model !== '') entry.model = model;
    if (providerId === 'codex') {
      if (form.modelReasoningEffort !== '') entry.modelReasoningEffort = form.modelReasoningEffort;
      if (form.webSearchMode !== '') entry.webSearchMode = form.webSearchMode;
    }
    if (Object.keys(entry).length > 0) assistants[providerId] = entry;
  }

  const body: UpdateAssistantConfigBody = { assistant: form.assistant };
  if (Object.keys(assistants).length > 0) body.assistants = assistants;
  return body;
}

// ---------------------------------------------------------------------------
// Model tiers. Types inlined (mirroring server/.../config.schemas.ts) until a
// regen lands TiersConfig / UpdateTiersBody / SafeConfig.tiers in
// @/lib/api.generated — same convention as skills/github.ts. Migrate to
// `components['schemas']['TiersConfig']` etc. once the spec is regenerated.
// ---------------------------------------------------------------------------

export type TierEntry = components['schemas']['TierEntry'];

export interface TiersMap {
  small?: TierEntry;
  medium?: TierEntry;
  large?: TierEntry;
}

/** `SafeConfig` + the tier fields not yet in the generated spec. */
export type SafeConfigTiers = SafeConfig & { tiers?: TiersMap; tierDefaults?: TiersMap };

export interface UpdateTiersBody {
  tiers: { small?: TierEntry | null; medium?: TierEntry | null; large?: TierEntry | null };
}

export function updateTiers(body: UpdateTiersBody): Promise<ConfigResponse> {
  return requestJson<ConfigResponse>('/api/config/tiers', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export type TierName = 'small' | 'medium' | 'large';
export const TIER_ORDER: readonly TierName[] = ['small', 'medium', 'large'];

export interface TierRowForm {
  provider: string; // '' = unset (falls back to the built-in default)
  model: string;
  effort: NonNullable<TierEntry['effort']> | '';
}

export type TiersForm = Record<TierName, TierRowForm>;

/**
 * Pure form → PATCH body. A row whose provider OR model is blank is sent as
 * `null` (unset → built-in default); a fully-set row carries `effort` when
 * present. The editor always sends all three tiers, so the per-key-merge route
 * cleanly applies sets and unsets in one call.
 */
export function buildTiersUpdate(form: TiersForm): UpdateTiersBody {
  const tiers: UpdateTiersBody['tiers'] = {};
  for (const tier of TIER_ORDER) {
    const row = form[tier];
    const provider = row.provider.trim();
    const model = row.model.trim();
    if (provider && model) {
      const entry: TierEntry = { provider, model };
      if (row.effort) entry.effort = row.effort;
      tiers[tier] = entry;
    } else {
      tiers[tier] = null;
    }
  }
  return { tiers };
}

// ---------------------------------------------------------------------------
// @custom aliases (install scope) + per-user AI prefs (Phase 3). Types inlined
// until a regen lands UserAiPrefs / UpdateAliasesBody in @/lib/api.generated —
// same convention as the tiers block above.
// ---------------------------------------------------------------------------

/** `SafeConfig` + the alias field not yet in the generated spec. */
export type SafeConfigAliases = SafeConfig & { aliases?: Record<string, TierEntry> };

export interface UpdateAliasesBody {
  aliases: Record<string, TierEntry | null>;
}

/** Install-wide @custom aliases — writes `aliases:` to ~/.archon/config.yaml. */
export function updateAliases(body: UpdateAliasesBody): Promise<ConfigResponse> {
  return requestJson<ConfigResponse>('/api/config/aliases', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * The current web user's personal AI prefs (raw per-user layer, not merged
 * with config). Reads 401 when no web identity resolves — panels use that to
 * hide the "Just me" scope.
 */
export interface UserAiPrefs {
  tiers?: TiersMap;
  aliases?: Record<string, TierEntry>;
  defaultProvider?: string;
  /** Per-user default CHAT model (#1998) — only meaningful with defaultProvider. */
  defaultModel?: string;
}

export function getUserAiPrefs(): Promise<UserAiPrefs> {
  return requestJson<UserAiPrefs>('/api/auth/me/ai-prefs');
}

export function updateUserTiers(body: UpdateTiersBody): Promise<UserAiPrefs> {
  return requestJson<UserAiPrefs>('/api/auth/me/ai-prefs/tiers', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function updateUserAliases(body: UpdateAliasesBody): Promise<UserAiPrefs> {
  return requestJson<UserAiPrefs>('/api/auth/me/ai-prefs/aliases', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Set the per-user default assistant + default chat model. Written atomically
 * by the server: an omitted/null model clears any previous pin, and a model
 * without a provider is rejected (400).
 */
export function updateUserDefault(
  provider: string | null,
  model: string | null
): Promise<UserAiPrefs> {
  return requestJson<UserAiPrefs>('/api/auth/me/ai-prefs/default', {
    method: 'PATCH',
    body: JSON.stringify({ provider, model }),
  });
}

/** The editable scope of a settings panel: install-wide config vs per-user DB prefs. */
export type SettingsScope = 'install' | 'user';

/** One editable alias row in the AliasesPanel. */
export interface AliasRowForm {
  name: string;
  provider: string;
  model: string;
  effort: NonNullable<TierEntry['effort']> | '';
}

/** Seed editable alias rows from a saved alias map (sorted by name). */
export function seedAliasRows(map: Record<string, TierEntry> | undefined): AliasRowForm[] {
  return Object.entries(map ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, e]) => ({ name, provider: e.provider, model: e.model, effort: e.effort ?? '' }));
}

/**
 * Pure form → PATCH body. Baseline names that no longer appear in the rows are
 * sent as `null` (unset — covers both deletion and rename), complete rows are
 * sent as entries, and incomplete rows (blank name/provider/model) are dropped.
 */
export function buildAliasesUpdate(
  rows: AliasRowForm[],
  baselineNames: readonly string[]
): UpdateAliasesBody {
  const aliases: UpdateAliasesBody['aliases'] = {};
  const present = new Set(rows.map(r => r.name.trim()).filter(n => n !== ''));
  for (const name of baselineNames) {
    if (!present.has(name)) aliases[name] = null;
  }
  for (const row of rows) {
    const name = row.name.trim();
    const provider = row.provider.trim();
    const model = row.model.trim();
    if (!name || !provider || !model) continue;
    const entry: TierEntry = { provider, model };
    if (row.effort) entry.effort = row.effort;
    aliases[name] = entry;
  }
  return { aliases };
}
